// @flow
import * as React from 'react';
import type { Cable, CablePath, PermitDocument } from 'esm-web-pro/types/networks';
import { FieldWrapper } from 'speed-js-react-web';
import { FormattedMessage } from 'react-intl';
import BaseSelect from 'esm-web-pro/components/forms/fields/BaseSelect';
import InfrastructureDocumentPermitForm
    from 'esm-web-pro/containers/pages/network/forms/InfrastructureDocumentPermitForm';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';
import { Box } from 'grommet';
import UseHighlightCable from 'esm-web-pro/containers/pages/network/objects/cable/useHighlightCable';
import { numberPointsCallbacksCablePath } from 'esm-web-pro/containers/pages/network/objects/cable/CablePaths';
import { EditorContext } from 'esm-web-pro/containers/map/editor/BaseEditor';
import { useLeaflet } from 'react-leaflet';


type Props = {
    cable: Cable,
    onClose: Function,
    onSave: Function,
};

const CableDocumentPermitForm = (props: Props) => {
    const { cable, onClose, onSave } = props;
    const { cable_path: { cable_paths: _cablePaths } } = cable;
    const cablePaths = React.useMemo(() => (_cablePaths || []));

    const [chosenCablePaths, setChosenCablePaths] = React.useState([]);
    const editor = React.useContext(EditorContext);
    const { map } = useLeaflet();
    const callbacks = React.useRef({});

    React.useEffect(() => {
        editor.unregisterAllLoadingCallback();
        const { cable_path: cablePath } = cable;
        if (cablePath && cablePath.path) {
            const cableCoordinates = cablePath.path.map((c) => [c[1], c[0]]);

            if (map) {
                // Go to the middle path of the cable
                map.highlightedGeometry = {
                    bounds: cableCoordinates, center: null,
                };
                map.panInsideBounds(cableCoordinates);
            }
        }
    }, [editor, cable, map]);

    const handleChooseCablePath = (cablePathUuids) => {
        const chosenCablePathsUuids = chosenCablePaths.map((cP) => cP.uuid);
        if (cablePathUuids.length > chosenCablePaths.length) {
            cablePaths
                .filter((cP) => !chosenCablePathsUuids.includes(cP.uuid) && cablePathUuids.includes(cP.uuid))
                .forEach((cP) => {
                    const coords = cP.path.coordinates.map((c) => [c[1], c[0]]);
                    const cableCallback = UseHighlightCable(coords);
                    const pointCallbacks = numberPointsCallbacksCablePath(cP.container_points);

                    callbacks.current[cP.uuid] = [cableCallback, ...pointCallbacks];
                    editor.registerMultipleLoadingCallbacks([cableCallback, ...pointCallbacks]);
                    editor.reloadFeatures();
                });
        } else {
            cablePaths
                .filter((cP) => chosenCablePathsUuids.includes(cP.uuid) && !cablePathUuids.includes(cP.uuid))
                .forEach((cP) => {
                    editor.unregisterMultipleLoadingCallbacks(callbacks.current[cP.uuid]);
                    editor.reloadFeatures();
                    callbacks.current[cP.uuid] = [];
                });
        }
        setChosenCablePaths(
            cablePaths.filter((c) => cablePathUuids.includes(c.uuid)),
        );
    };

    const renderCablePathSelect = () => {
        const cablePathChoices = cablePaths.reduce((cablePathsResult, c) => {
            const { sequence_number: sequenceNumber, provider_ref: providerRef } = c;

            let optionText = sequenceNumber;
            if (providerRef) {
                optionText += ` ${providerRef}`;
            }

            cablePathsResult[c.uuid] = optionText;
            return cablePathsResult;
        }, {});

        return (
            <FieldWrapper label={(
                <FormattedMessage
                    id="cable-path-select-title"
                    defaultMessage="Cable path"
                />
            )}
            >
                <BaseSelect
                    emptyLabel={(
                        <FormattedMessage
                            id="cable-path-select"
                            defaultMessage="Select cable path..."
                        />
                    )}
                    choices={cablePathChoices}
                    value={chosenCablePaths?.map((cP) => cP.uuid) || null}
                    onChange={handleChooseCablePath}
                    multi
                />
            </FieldWrapper>
        );
    };

    const handleSave = (newDocuments: PermitDocument[]) => {
        const newCablePaths = [...cablePaths];
        newCablePaths.forEach((cP) => {
            if (chosenCablePaths.map((c) => c.uuid).includes(cP.uuid)) {
                const { permit_documents: permitDocuments } = cP;
                const newDocument = newDocuments.find((document) => document.object_id === cP.uuid);
                cP.permit_documents = [...permitDocuments, newDocument];
            }
        });

        onSave({ cable_path: { ...cable.cable_path, cable_paths: newCablePaths } });
    };

    return (
        <>
            <Box pad={{ horizontal: 'medium' }} height={{ min: 'min-content' }}>
                {renderCablePathSelect()}
            </Box>
            {chosenCablePaths?.length ? (
                <InfrastructureDocumentPermitForm
                    onClose={onClose}
                    onSave={handleSave}
                    ids={chosenCablePaths.map((cP) => cP.uuid)}
                    model="cablepath"
                    many
                />
            ) : (
                <ActionButtons onClose={onClose} onSave={null} />
            )}
        </>
    );
};

export default CableDocumentPermitForm;
